import faqEn from './faq/en';

const enTranslations = {
    accountAccess: {
        emailLabel: 'Email',
        phoneLabel: 'Mobile number',
        phoneHint: 'Norwegian number, or include the country code',
        useEmail: 'Use email instead',
        usePhone: 'Use mobile number instead',
        sendCode: 'Send code',
        codeLabel: '6-digit code',
        confirmCode: 'Confirm code',
        codeSentTo:
            'We sent a 6-digit code to {{contact}}. It is valid for {{duration}}.',
        duration: {
            minutes: '{{count}} min',
            hours: '{{count}} hours',
            days: '{{count}} days',
        },
        deliveryFailed:
            'We could not send a code to {{contact}}. Send a new code, or use a different email or mobile number.',
        resend: 'Send a new code',
        resendIn: 'Send a new code in {{time}}',
        useDifferentContact: 'Use a different email or mobile number',
        passwordRules:
            'Use at least 8 characters, with an uppercase letter, a lowercase letter and a number.',
        activation: {
            codeExpiredResent:
                'Your invitation code had expired, so we sent you a new one.',
            title: 'Activate your invitation',
            description:
                'Use the email or mobile number your care home invited you with. You will get a code, and then choose your own password.',
            submit: 'Activate account',
            steps: {
                contact: '1. Where did you get the invitation?',
                code: '2. Enter the code',
                password: '3. Choose your password',
            },
        },
        contacts: {
            notAdded: 'Not added',
            addEmail: 'Add email',
            addPhone: 'Add mobile number',
            changeEmail: 'Change email',
            changePhone: 'Change mobile number',
            confirmEmail: 'Confirm email',
            confirmPhone: 'Confirm mobile number',
            confirmAction: 'Confirm',
            confirmNote:
                'We will send a code to confirm it. After that you can use it to log in and reset your password.',
            add: 'Add',
            change: 'Change',
            newEmail: 'New email',
            newPhone: 'New mobile number',
            keepsWorking:
                'You can keep using {{contact}} until the new one is confirmed.',
            confirm: 'Confirm and save',
            saved: 'Saved. You can now log in with {{contact}}.',
        },
        staffContacts: {
            pendingNote: 'Invitation not activated yet.',
        },
        invitation: {
            sendEmail: 'Send by email',
            sendSms: 'Send by SMS',
            sent: 'Invitation sent to {{contact}}.',
            sendBy: 'Send invitation by',
            byEmail: 'Email',
            bySms: 'SMS',
            contactRequired: 'Enter an email or a Norwegian mobile number.',
        },
        errors: {
            CONTACT_CONFIRMED:
                'This contact is confirmed. Only the person can change it, in their profile.',
            LAST_INVITATION_CONTACT:
                'An invitation needs an email or phone. Add the other one before removing this.',
            rateLimited:
                'Too many codes have been sent. Try again in {{duration}}.',
            CODE_INVALID:
                'That code is not correct. Check it, or send a new code.',
            CODE_EXPIRED: 'The code has expired. Send a new code.',
            CONTACT_EXISTS:
                'This email or mobile number is already used by another account.',
            CONTACT_NOT_VERIFIED:
                'This email or mobile number is not confirmed yet. Log in with the one you activated with, then confirm it under My profile.',
            CONTACT_NOT_FOUND:
                'We could not find an invitation for this. Check with the care home that they have the right details.',
            ACCOUNT_ACTIVE:
                'This account is already active. Log in, or reset your password.',
            SIGN_IN_REQUIRED:
                'Your account is ready. Log in with your new password.',
            SESSION_EXPIRED: 'This took too long. Please start again.',
            ACTIVATION_REQUIRED: 'This account is not activated yet.',
        },
    },
    monthPeriod: {
        previous: 'Previous month',
        next: 'Next month',
        choose: 'Choose month',
    },
    supportMode: {
        title: 'Support mode · {{organization}}',
        readOnly: 'Read-only access. Message reads are not recorded.',
        exit: 'Exit',
        exiting: 'Exiting…',
    },
    account: {
        loggedIn: 'Logged in',
        profile: 'Profile',
        faq: 'FAQ',
        logout: 'Log out',
        languageLabel: 'Language',
    },
    onboarding: {
        openGuide: 'Open onboarding guide',
        roles: {
            organization: [
                {
                    id: '1',
                    title: 'Welcome to Mundu!',
                    description:
                        'Hi, {{username}}! You are registered as a Superuser for {{organizationName}} in Mundu. This guide explains how you can help {{organizationName}} get started and gives you a quick overview of your possibilities in Mundu.',
                },
                {
                    id: '2',
                    title: 'Superuser',
                    description:
                        'As a Superuser you manage Mundu for {{organizationName}}. You control access and can create or delete Users, Care Contacts, and Relatives.',
                },
                {
                    id: '3',
                    title: 'Create Users',
                    description:
                        'Create Users so you have someone to build videos for. Users can log in on a tablet or phone to receive and play the videos you make.',
                },
                {
                    id: '4',
                    title: 'Invite relatives',
                    description:
                        'Invite the user’s relatives so they can share photos you may use in the videos.',
                },
                {
                    id: '5',
                    title: 'Upload photos',
                    description:
                        'You can also upload photos on behalf of relatives for every User in {{organizationName}}. The photos are ready when you start creating a video.',
                },
                {
                    id: '6',
                    title: 'Map the musical life story',
                    description:
                        'Browse the music library and note the songs and artists that matter most to the user. Good preparation makes selecting meaningful music easier later on.',
                },
                {
                    id: '7',
                    title: 'Create video',
                    description:
                        'Combine photos from relatives and music from the library. When you are happy, send the finished video to the user’s tablet.',
                },
                {
                    id: '8',
                    title: 'Mundu is on mobile too!',
                    description:
                        'Want to scan physical photos or share directly from a phone or tablet? Download the Mundu app using the QR code to find it in the App Store or Google Play.',
                    type: 'qr',
                },
            ],
            creator: [
                {
                    id: '1',
                    title: 'Welcome to Mundu!',
                    description:
                        'Hi, {{username}}! You are registered as a Care Contact for {{organizationName}} in Mundu. This guide helps you get started.',
                },
                {
                    id: '2',
                    title: 'Care Contact',
                    description:
                        'As a Care Contact you can create Users for {{organizationName}}, invite relatives, make videos, and upload photos for the Users you follow.',
                },
                {
                    id: '3',
                    title: 'Create Users',
                    description:
                        'Create Users so you have someone to build video stories for. Users can log in on tablets or phones to receive the videos.',
                },
                {
                    id: '4',
                    title: 'Invite relatives',
                    description:
                        'Invite the user’s relatives so they can share photos for your videos.',
                },
                {
                    id: '5',
                    title: 'Upload photos',
                    description:
                        'You can upload photos on behalf of relatives for Users you support. The photos appear automatically while editing the video.',
                },
                {
                    id: '6',
                    title: 'Map the musical life story',
                    description:
                        'Explore the music library and capture the songs that mean the most to the user. They are ready when you assemble the final video.',
                },
                {
                    id: '7',
                    title: 'Create video',
                    description:
                        'Blend photos and music to make a personal video, then send it to the user’s tablet once it is ready.',
                },
                {
                    id: '8',
                    title: 'Mundu is on mobile too!',
                    description:
                        'Download the Mundu app to scan photos or share from mobile and tablet via the QR code.',
                    type: 'qr',
                },
            ],
            nextOfKin: [
                {
                    id: '1',
                    title: 'Welcome to Mundu on the web!',
                    description:
                        'Hi, {{username}}! {{organizationName}} has registered you as a relative for {{consumerName}}. This guide shows you how to get started.',
                },
                {
                    id: '2',
                    title: 'Relatives',
                    description:
                        'As a relative you can share photos with {{organizationName}}. Care teams use them to create personal music videos for {{consumerName}}.',
                },
                {
                    id: '3',
                    title: 'Add photos',
                    description:
                        'Press “Upload photos” to begin. Use “Add photos” or drag and drop files directly into the selection area.',
                },
                {
                    id: '5',
                    title: 'Add captions',
                    description:
                        'Short captions make it easier for staff at {{organizationName}} to craft meaningful videos. Captions are only visible to the care team.',
                },
                {
                    id: '6',
                    title: 'Upload to the cloud',
                    description:
                        'When your selection is ready, confirm consent in the side panel and upload. The Care Contact for {{consumerName}} is notified when the photos arrive.',
                    type: 'cloud',
                },
                {
                    id: '7',
                    title: 'Mundu on mobile',
                    description:
                        'Download the Mundu app to scan physical photos or share directly from mobile and tablet via the QR code.',
                    type: 'qr',
                },
            ],
            default: [
                {
                    id: '1',
                    title: 'Welcome to Mundu on the web!',
                    description: 'Press enter to get started',
                },
            ],
        },
    },
    search: {
        musicPlaceholder: 'Search for music',
        genericPlaceholder: 'Search',
    },
    common: {
        accountStatusFilters: {
            title: 'Account status',
            all: 'All',
            active: 'Active',
            invited: 'Invited',
        },
        cancel: 'Cancel',
        close: 'Close',
        edit: 'Edit',
        search: 'Search',
        addRemove: 'Add / remove',
        save: 'Save',
        confirm: 'Confirm',
        loading: 'Loading…',
        upload: 'Upload',
        remove: 'Remove',
        delete: 'Delete',
        confirmDelete: "Type '{{value}}' to delete",
        name: 'Name',
        phone: 'Phone',
        email: 'Email',
        actions: 'Actions',
        username: 'Username',
        group: 'Group',
    },
    browseMusic: {
        favorites: {
            title: 'Musical identity',
            importance: 'Importance',
            likesSinging: 'Enjoys singing',
        },
        selection: {
            instructions: {
                playlist: 'Click “Select” to add a song from the playlist.',
                search: 'Click an album cover to add a song from the search results.',
                release: 'Click “Select” to add a song from the release.',
                artist: 'Click “Select” to add a song by the artist.',
            },
            title: 'Selected songs',
            empty: 'The selection is empty...',
            totalDuration: 'Total duration:',
            addButton: 'Add',
            addSuffix: 'songs',
            cancel: 'Cancel and go back',
        },
        search: {
            tracks: 'Songs',
            artists: 'Artists',
            albums: 'Albums',
            moreSongsPlaceholder: 'Search for more songs',
            noResults: {
                prefix: 'No results for ',
                hint: 'Check the spelling or try fewer/other keywords.',
                followUp:
                    'Missing a specific artist, release, or song? The Mundu catalog is expanding and new music will appear here once it is available.',
            },
        },
        playlist: {
            empty: 'The playlist is empty. Use the search field below to add songs.',
        },
        nav: {
            library: 'Library',
            favorites: 'Favorites',
        },
        common: {
            loadMore: 'Show more',
        },
    },
    alerts: {
        successRedirect: 'Click <link>here</link> to close the window.',
    },
    validation: {
        requiredField: 'This field is required.',
        invalidPhone: 'The phone number is not valid.',
        invalidEmail: 'The email address is not valid.',
        passwordsMismatch: 'Password and confirmation must match.',
        passwordTooShort: 'The password is too short.',
        usernameInvalid: 'Username cannot contain æ, ø or å.',
        passwordDisallowedChars: 'Password cannot contain æ, ø or å.',
        lettersAndSpacesOnly: 'Use letters and spaces only.',
        usernameAllowedChars:
            'Only letters A-Z, numbers, and the characters . - _ ! # ^ ~ are allowed.',
        lengthBetween: 'Text must be between {{min}} and {{max}} characters.',
        maxLength: 'Maximum {{max}} characters.',
        orgSlugNoDoubleHyphen:
            'Hyphen followed by hyphen ("--") is not allowed.',
        orgSlugAllowedChars:
            'Only lowercase letters, numbers, and hyphens are allowed.',
        orgSlugStartEnd: 'Must start and end with a letter or a number.',
    },
    home: {
        incomplete: {
            title: 'Unfinished videos',
            deleted: 'The video was deleted',
            confirmDelete: 'Are you sure you want to delete this video?',
        },
        shortcuts: {
            newUser: 'New resident',
            newCreator: 'New employee',
            newVideo: 'Create new video',
            uploadImages: 'Upload photos',
        },
    },
    nextOfKin: {
        title: 'Invite relatives',
        success: 'Relative created successfully',
        form: {
            firstName: 'First name*',
            lastName: 'Last name*',
            phone: 'Phone number',
            email: 'Email*',
            relationLabel: 'Relationship',
            selectRelation: 'Select relationship',
            relationFamily: 'Family or close relationship',
            relationGuardian: 'External person',
            isMainContactLabel: 'Main contact',
            isGuardianLabel: 'Guardian',
            disableManagingRelativesLabel:
                "Disable this person's ability to manage relatives",
            submit: 'Send invitation',
        },
        card: {
            deleteButton: 'Delete',
            deleteConfirm: 'Are you sure you want to delete this contact?',
            editButton: 'Edit',
            editTitle: 'Edit relative',
            editDescription: 'Update the contact details for {{name}}.',
        },
        list: {
            title: 'Relatives',
            empty: 'No relatives added yet.',
        },
        actions: {
            new: 'New relative',
        },
        notifications: {
            deleted: 'Relative deleted.',
            updated: 'Relative updated.',
            alreadyLinked: 'This relative is already linked to the resident.',
            emailBelongsToOtherRole:
                'This email is already used by a user who is not a relative.',
        },
    },
    profile: {
        picture: {
            label: 'Profile picture',
            change: 'Change picture',
            remove: 'Remove picture',
        },
        labels: {
            phone: 'Phone number',
            email: 'Email',
            firstName: 'First name*',
            lastName: 'Last name*',
            username: 'Username',
        },
        organization: {
            sectionTitle: 'Licences',
            usageSuffix: 'in use',
        },
        delete: {
            title: 'Delete account?',
            description: 'Deleting the account removes your access to Mundu.',
            confirmLabel: "Type '{{value}}' to delete",
            button: 'Delete account',
        },
        language: 'Language',
    },
    music: {
        validation: {
            playlistNameRequired: 'The playlist must have a name.',
        },
    },
    session: {
        validation: {
            videoNameRequired: 'The video name is required.',
        },
    },
    errors: {
        emailInUse: 'Email address is already in use.',
        phoneInUse: 'Phone number is already in use.',
        usernameInUse: 'Username is already taken.',
        loginFailed: 'Could not sign in. Please try again.',
        accountNotActivated: 'The account has not been activated yet.',
        missingAccessToken:
            'Response did not include an access token. Please sign in again.',
        invalidCredentials: 'Incorrect username or password.',
        refreshFailed: 'Could not refresh the sign-in session.',
        unknownRefresh: 'Unknown error while refreshing token.',
        unknownLogin: 'Unknown error while signing in.',
        passwordChangeRequired:
            'You need to set a new password before signing in.',
    },
    roles: {
        admin: 'Admin',
        systemAdmin: 'System admin',
        organization: 'Superuser',
        creator: 'Employee',
        nextOfKin: 'Relative',
        consumer: 'Resident',
        unknown: 'Role',
    },
    messages: {
        unsavedChanges:
            'You have unsaved changes. Are you sure you want to leave this page?',
    },
    modals: {
        confirmExitTitle: 'Do you want to exit?',
    },
    notifications: {
        authRequired: 'You must be signed in to access this page.',
        accessDenied: 'You do not have access to this page.',
        profileUpdated: 'Profile updated.',
    },
    webPushPermission: {
        title: 'Enable browser notifications',
        description:
            'Get event reminders and alerts when relatives send messages or comments.',
        blockedTitle: 'Notifications are blocked',
        blockedDescription:
            'Enable notifications for this site in your browser settings to receive event, message and comment alerts.',
        enable: 'Enable notifications',
        enabling: 'Enabling...',
        notNow: 'Not now',
    },
    nav: {
        home: 'Home',
    },
    modules: {
        moduleNavigation: {
            title: 'Modules',
            notEnabled: 'Not enabled',
            accessRequired: 'No access',
        },
        modulePages: {
            navigationLabel: 'Module pages',
            main: 'Main',
            settings: 'Settings',
            settingsDescription:
                'View access and settings for the {{module}} module.',
        },
        moduleEmployeeReadAccess: {
            title: 'Employee access',
            description:
                'Choose whether every employee can view the {{module}} module.',
            label: 'Allow all employees to view this module',
            helper: 'Employees who are not module members receive read-only access. Module members can continue to manage its content.',
            saving: 'Saving…',
            updated: 'Employee access updated.',
        },
        moduleUnavailable: {
            notEnabledTitle: '{{module}} is not enabled',
            notEnabledDescription:
                'This module is available, but it has not been enabled for your organization.',
            notEnabledHelp:
                'Contact an organization administrator or Mundu if you would like to start using it.',
            accessRequiredTitle: 'Access to {{module}} is required',
            accessRequiredDescription:
                'This module is enabled, but your account has not been added as a member.',
            accessRequiredHelp:
                'Ask an organization administrator to add you to the module.',
        },
        admin: {
            title: 'Admin',
            navOrganizations: 'Organizations',
            newOrganization: 'New organization',
            editOrganization: 'Edit organization',
            newAdmin: 'New admin',
            organizationDetail: 'Organization | {{name}}',
        },
        users: {
            headerNew: 'New resident',
            navLabel: 'Residents',
            navNew: 'New resident',
            mainContacts: 'Main contacts',
            empty: 'No residents yet',
            mainContactFilters: {
                title: 'Residents by main-contact login status',
                all: 'All',
                active: 'Active',
                invited: 'Invited',
                empty: {
                    active: 'No residents with an active main contact match these filters.',
                    invited:
                        'No residents with only invited main contacts match these filters.',
                },
                noMatches: 'No residents match your search or group filters.',
            },
        },
        creators: {
            headerDefault: 'Employees',
            headerNew: 'New employee',
            headerProfile: 'Employees / profile',
            navLabel: 'Employees',
            navNew: 'New employee',
            empty: 'No employees yet',
        },
        myProfile: {
            title: 'My profile',
        },
        organization: {
            navLabel: 'Organization',
            settings: 'Settings',
            documents: 'Documents',
        },
        appContent: {
            navLabel: 'Documents',
            documentsTitle: 'Documents',
            documentCount_one: '{{count}} document',
            documentCount_other: '{{count}} documents',
            noDocuments: 'No documents have been shared yet.',
            readOnlyLabel: 'Read only',
            readOnlyNotice:
                'You can view these documents. Only module members can make changes.',
            members: {
                settingsDescription:
                    'Members can manage the documents residents and relatives see in the Mundu app.',
            },
        },
        calendar: {
            navLabel: 'Calendar',
            title: 'Calendar',
            newEvent: 'New event',
            notEnabled:
                'The calendar module is not enabled for this organization.',
            responseStatus: {
                tabLabel: 'Responses',
                replyProgress: '{{replied}} / {{total}} replied',
                noInvitees: 'No invitees',
                title: 'Response status',
                reminder: {
                    sendWithCount: 'Send response reminder to {{count}} people',
                    send: 'Send response reminder',
                    retry: 'Retry failed · {{count}}',
                    confirm:
                        'Send a response reminder to {{count}} people waiting for a reply?',
                    result: 'Sent: {{sent}}. Failed: {{failed}}.',
                    history:
                        'Latest reminders: {{sent}} sent, {{failed}} failed.',
                    sent: 'Reminder sent',
                    failed: 'Reminder failed',
                    error: 'Response reminders could not be sent. Try again.',
                },
                totalInvited: 'Total invited',
                going: 'Going',
                notGoing: 'Not going',
                waitingForReply: 'Waiting for reply',
                noResponse: 'No response',
                relativeOf: 'Relative of',
                all: 'All',
                responseColumn: 'Response',
                respondedAt: 'Replied',
                closedNotice: 'Replies closed {{date}}. This list is final.',
                unnamed: 'Name not added',
                empty: 'No one here yet.',
                loadError: 'Response status could not be loaded. Try again.',
            },
            form: {
                createTitle: 'New event',
                editTitle: 'Edit event',
                detailsTab: 'Details',
                viewTitle: 'Event details',
                readOnlyLabel: 'Read-only event',
                cancelledLabel: 'Cancelled',
                cancelledNotice: 'This event has been cancelled.',
                cancelledManageNotice:
                    'This event has been cancelled. Restore it to edit it again.',
                readOnlyNotice:
                    'Only the employee who created this event or an organization administrator can edit or delete it.',
                moduleMembershipReadOnlyNotice:
                    'You can view calendar events, but only Calendar module members can create events and manage events they created.',
                detailsSectionTitle: 'Event details',
                organizerLabel: 'Event organiser',
                organizerUnknown: 'Unknown organiser',
                scheduleSectionTitle: 'Date and time',
                titleLabel: 'Title',
                descriptionLabel: 'Description',
                locationLabel: 'Location',
                typeLabel: 'Event type',
                removeRoomsToUseOpen:
                    'Remove individual room selections to use Open to all.',
                groupAudienceHint:
                    'Group events require selected departments, groups, or rooms.',
                types: {
                    residents: {
                        label: 'Open to all',
                        description:
                            'For everyone in selected departments or groups, or the whole organization. Relatives can see the event but are not notified.',
                    },
                    internal: {
                        label: 'Internal',
                        description: 'Visible only to employees.',
                    },
                    group: {
                        label: 'Group',
                        description:
                            'For selected departments, groups, and individual rooms. Their relatives can see the event but are not notified.',
                    },
                    relatives: {
                        label: 'Relatives',
                        description:
                            'Visible to invited residents, relatives and employees. Invited relatives receive notifications.',
                    },
                },
                requestReplyTitle: 'Request reply',
                requireResponse: 'Require response (main contacts only)',
                requireResponseSeriesOnly:
                    'Edit the whole series to change this setting.',
                requireResponseFutureStart:
                    'Move the event start into the future to request replies.',
                disableResponseConfirm:
                    'Stop asking for replies to upcoming occurrences? Existing answers and past response status will be kept.',
                rsvpScheduleChangeConfirm:
                    'Past replies and surviving occurrences are kept. Replies to removed future occurrences will be deleted.',
                rsvpScheduleChangeTitle: 'Change event schedule?',
                rsvpScheduleChangeProceed: 'Change schedule',
                residentAudienceCount: '{{value}} residents',
                residentAudienceCount_one: '{{count}} resident',
                residentAudienceCount_other: '{{count}} residents',
                relativeAudienceCount: '{{value}} relatives',
                relativeAudienceCount_one: '{{count}} relative',
                relativeAudienceCount_other: '{{count}} relatives',
                mainContactAudienceCount: '{{value}} main contacts',
                mainContactAudienceCount_one: '{{count}} main contact',
                mainContactAudienceCount_other: '{{count}} main contacts',
                mainContactInvitationCount:
                    '{{value}} main contacts will receive the invitation',
                mainContactInvitationCount_one:
                    '{{count}} main contact will receive the invitation',
                mainContactInvitationCount_other:
                    '{{count}} main contacts will receive the invitation',
                audienceCountUnavailable: 'Unavailable',
                audiencePreviewError: 'Could not load audience counts.',
                colorLabel: 'Event color',
                internalColor: 'Internal event',
                iconLabel: 'Event icon',
                noIcon: 'No icon',
                icons: {
                    calendar: 'General event',
                    singing: 'Singing',
                    music: 'Music',
                    dancing: 'Dancing',
                    hiking: 'Hiking',
                    outdoors: 'Outdoors',
                    park: 'Park',
                    gardening: 'Gardening',
                    dinner: 'Dinner',
                    coffee: 'Coffee',
                    drinks: 'Bar and drinks',
                    baking: 'Baking',
                    games: 'Games',
                    reading: 'Reading',
                    film: 'Film',
                    arts: 'Arts',
                    crafts: 'Crafts',
                    exercise: 'Exercise',
                    sports: 'Sports',
                    swimming: 'Swimming',
                    trip: 'Trip',
                    celebration: 'Celebration',
                    social: 'Social',
                    wellbeing: 'Wellbeing',
                    animals: 'Animals',
                },
                colors: {
                    plum: 'Plum',
                    terracotta: 'Terracotta',
                    coral: 'Coral',
                    rose: 'Rose',
                    violet: 'Violet',
                    blue: 'Blue',
                    teal: 'Teal',
                    green: 'Green',
                    olive: 'Olive',
                    gold: 'Gold',
                },
                startLabel: 'Starts',
                endLabel: 'Ends',
                allDayLabel: 'All-day event',
                recurrenceLabel: 'Repeats',
                doesNotRepeat: 'Does not repeat',
                recurrenceDaily: 'Daily',
                recurrenceWeekly: 'Weekly',
                recurrenceWeeklyOn: 'Weekly on {{weekday}}',
                recurrenceMonthly: 'Monthly',
                recurrenceMonthlyOnDay: 'Monthly on day {{day}}',
                recurrenceUntilLabel: 'Repeat until',
                recurrenceUntilRequired: 'Select when the event series ends.',
                recurrenceUntilBeforeStart:
                    'The event series cannot end before it starts.',
                seriesEditNotice:
                    'Changes apply to the recurring series. Existing changes to individual events are kept.',
                occurrenceEditNotice:
                    'The repeat settings, event type, and attendees stay the same for the series.',
                seriesExceptionResetNotice:
                    'Changing the series start or repeat pattern removes changes made to individual events.',
                editRecurringTitle: 'Edit recurring event',
                editRecurringPrompt: 'Which events do you want to change?',
                thisEvent: 'This event',
                allEvents: 'All events',
                attendeesLabel: 'Attendees',
                attendeeCount: '{{count}} people',
                internalLabel: 'Internal event – visible only to employees',
                sendToAllLabel: 'Everyone in the organization',
                sendToAllLabelWithOrg: 'Everyone at {{organizationName}}',
                targetsRequired: 'Select at least one group or everyone.',
                endBeforeStart: 'The event cannot end before it starts.',
                submit: 'Create event',
                update: 'Save changes',
                discardConfirmTitle: 'Discard changes?',
                discardConfirm:
                    'You have unsaved changes. If you leave now, your changes will be lost.',
                keepEditing: 'Keep editing',
                discardChanges: 'Discard changes',
                delete: 'Delete event',
                cancelEvent: 'Cancel event',
                keepEvent: 'Keep event',
                deleteSeries: 'Delete series',
                deleteThisEvent: 'Delete this event',
                deleteAllEvents: 'Delete all events',
                deleteConfirmTitle: 'Delete event?',
                deleteConfirm:
                    'Delete "{{title}}" permanently? It will no longer be visible to anyone.',
                deleteInternalConfirm:
                    'Are you sure you want to delete the internal event "{{title}}"?',
                deleteSeriesConfirmTitle: 'Delete recurring event?',
                deleteSeriesConfirm:
                    'Delete the entire "{{title}}" series permanently? It will no longer be visible to anyone.',
                deleteInternalSeriesConfirm:
                    'Are you sure you want to delete the entire internal event series "{{title}}"? All occurrences will be removed.',
                deleteRecurringPrompt:
                    'Delete only this event, or every event in the series?',
                cancelConfirmTitle: 'Cancel event?',
                cancelSeriesConfirmTitle: 'Cancel recurring event?',
                cancelConfirm:
                    'Cancel "{{title}}"? It will remain visible as cancelled and attendees will be notified.',
                cancelRecurringPrompt:
                    'Cancel only this event, or every event in the series?',
                cancelThisEvent: 'Cancel this event',
                cancelAllEvents: 'Cancel all events',
                restoreEvent: 'Restore event',
            },
            notifications: {
                created: 'Event created.',
                updated: 'Event updated.',
                deleted: 'Event deleted.',
                cancelled: 'Event cancelled.',
                restored: 'Event restored.',
            },
            tabs: {
                ariaLabel: 'Calendar sections',
                calendar: 'Calendar',
                myEvents: 'My events',
                readView: 'Print view',
            },
            readView: {
                printOrSave: 'Print or save',
            },
            myEvents: {
                filterAriaLabel: 'Filter my events',
                allInDepartment: 'All in my department',
                createdByMe: 'Created by me',
                departmentShort: 'My department',
                createdByMeShort: 'Created by me',
            },
            members: {
                manage: 'Manage members',
                title: 'Calendar module members',
                helper: 'Members can see and manage the calendar. Add employees from the list below.',
                settingsDescription:
                    'Members can create and edit events. Everyone in the organization can still view the calendar.',
                count: '{{count}} members',
                countWithLimit: '{{count}} of {{limit}} members',
                limitReached:
                    'The member limit ({{limit}}) is reached. Remove a member to add someone else.',
                updated: 'Members updated.',
            },
            views: {
                allDay: 'All day',
                viewPickerAriaLabel: 'Choose calendar view',
                previous: 'Back',
                next: 'Next',
                today: 'Today',
                month: 'Month',
                week: 'Week',
                weekNumber: 'Week {{week}}',
                day: 'Day',
                agenda: 'List',
                date: 'Date',
                time: 'Time',
                event: 'Event',
                now: 'Now {{time}}',
                recurringEvent: 'Recurring event',
                noEventsInRange: 'No events in this period.',
                showMore: '+{{total}} more',
            },
        },
        messages: {
            navLabel: 'Messages',
            listTitle: 'Lastest messages',
            createTitle: 'Send a new message',
            actions: 'Message actions',
            edit: 'Edit message',
            editTitle: 'Edit message',
            editSave: 'Save changes',
            editSuccess: 'Message updated.',
            deleteConfirmTitle: 'Delete message?',
            deleteConfirmMessage:
                'Are you sure you want to delete this message?',
            backToList: 'Back to messages',
            listEmpty: 'No messages have been sent yet.',
            listRefreshing: 'Updating messages…',
            deleteSuccess: 'Message deleted.',
            targetAll: 'Sent to everyone',
            target: 'Send to {{value}}',
            targetRelatives: 'Sent to relatives of {{value}}',
            sentBy: 'by {{value}}',
            sentByUnknown: 'by unknown user',
            commentsOpen: 'Open comments',
            commentsClose: 'Close comments',
            commentsOpened: 'Comments opened.',
            commentsClosedNotice: 'Comments closed.',
            commentsTitle: 'Comments',
            commentsEmpty: 'No comments yet.',
            commentsClosed: 'Comments are closed.',
            commentsShowOlder: 'Show older comments ({{count}})',
            commentsShowLatest: 'Show latest {{count}} comments',
            commentsEdit: 'Edit',
            commentsEditSave: 'Save',
            commentsEditSuccess: 'Comment updated.',
            commentsDeleteSuccess: 'Comment deleted.',
            commentsDeleteConfirmTitle: 'Delete comment?',
            commentsDeleteConfirmMessage:
                'Are you sure you want to delete this comment?',
            commentsLabel: 'Write comment:',
            commentsSubmit: 'Send comment',
            commentsViewsButtonLabel:
                'Show who opened the message after this comment',
            commentsNoViews: 'No one yet.',
            viewsViewedBy: 'Viewed by',
            viewsUnknownViewer: 'Unknown user',
            viewsAriaLabel: 'Views: {{count}}',
            filters: {
                title: 'Filters',
                all: 'All',
                personal: 'Personal',
                sentToAll: 'Sent to all',
                sentByMe: 'Sent by me',
                openDialogues: 'Open dialogues',
                unread: 'Unread',
                departments: 'Departments',
                departmentsPlaceholder: 'Select departments',
                residentGroups: 'Resident groups',
                residentGroupsPlaceholder: 'Select resident groups',
                levelPlaceholder: 'Select {{levelName}}',
                noStaffReply: 'No staff reply',
                noStaffReplyCount: '{{count}} conversations',
                noStaffReplyCount_one: '{{count}} conversation',
                period: 'Message period',
                allTime: 'All time',
                selectedMonth: 'Month',
                month: 'Month',
                analyticsHint: 'Matches the analytics dashboard',
                fromAnalytics: 'From analytics',
                clearAnalytics: 'Clear the filters from analytics',
            },
            form: {
                noOrganization:
                    'You must belong to an organization to view messages.',
                noPermission:
                    'You do not have permission to send messages in this organization.',
                subjectLabel: 'Subject*',
                bodyLabel: 'Message',
                recipientsLabel: 'Recipients*',
                recipientsTitle: 'Recipients',
                recipientsPlaceholder: 'Select relatives',
                recipientsEmpty: 'No relatives available.',
                recipientsRequired: 'Select at least one relative.',
                recipientsAllRelatives: 'All relatives',
                recipientsBadgeMainContact: 'Main contact',
                recipientsBadgeMainContactShort: 'M',
                recipientsBadgeGuardian: 'Guardian',
                recipientsBadgeGuardianShort: 'G',
                attachmentsLabel: 'Attachments (PDF, images, video)',
                unsupportedAttachmentType:
                    'Only JPEG, PNG, WebP, HEIC, PDF, or video files are allowed.',
                attachmentConversionFailed:
                    'This HEIC image could not be converted. Try exporting it as JPEG and attach it again.',
                sendToAllLabel: 'Send to all',
                sendToAllLabelWithOrg: 'Send to all at {{organizationName}}',
                sendToMainContactsLabel: 'Send only to main contacts',
                commentsEnabledLabel: 'Open for comments',
                emptyGroupsWarning:
                    'These groups have no residents assigned: {{groups}}',
                emptyGroupsCta: 'Assign residents',
                emptyGroupsCtaNonOrg: 'Contact {{name}} ({{email}})',
                noNextOfKinWarning:
                    'There are {{count}} residents without relatives assigned.',
                noNextOfKinCta: 'Add relatives in the Residents tab',
                targetsRequired:
                    'Select at least one recipient before sending the message.',
                submit: 'Send message',
            },
            notifications: {
                created: 'Message sent.',
            },
            attachmentsHeading: 'Attachments',
            attachmentsPreviousImage: 'Previous image',
            attachmentsNextImage: 'Next image',
            attachmentsSaveImage: 'Save image',
            attachmentsImageSaved: 'Image saved.',
            attachmentsImageSaveError: 'The image could not be saved.',
        },
        analytics: {
            navLabel: 'Analytics',
            members: {
                settingsDescription:
                    "Members can see this organization's analytics. Administrators always have access.",
            },
        },
        infoScreen: {
            navLabel: 'Info screen',
            header: 'Info screen',
            members: {
                manage: 'Manage members',
                title: 'Info screen module members',
                helper: 'Members can see and manage the info screen. Add employees from the list below.',
                settingsDescription:
                    'Members can manage the info screen content and TV setup.',
                updated: 'Members updated.',
            },
            loading: 'Loading info screen…',
            readOnlyNotice:
                'You can view this module. Only module members and organization administrators can make changes.',
            readOnlySlidesShown: 'Active slides shown on the TV: {{count}}',
            slidesTitle: 'Slides',
            slidesShown: '{{active}} of {{total}} slides are shown on the TV',
            saveChanges: 'Save changes',
            saving: 'Saving…',
            saved: 'Info screen saved.',
            unsavedChanges: 'You have unsaved changes',
            secondsPerSlide: 'Seconds per slide',
            addSlide: 'Add slide',
            uploading: 'Uploading…',
            reorderHint:
                'Drag slides, or use the ‹ › buttons on the selected slide, to change the order they play in.',
            slideCounter: 'Slide {{current}} of {{total}}',
            moveEarlier: 'Move earlier',
            moveLater: 'Move later',
            prevSlide: 'Previous slide',
            nextSlide: 'Next slide',
            hiddenBadge: 'Hidden',
            shownOnTv: 'Shown on the TV',
            hiddenFromTv: 'Hidden from the TV',
            deleteSlide: 'Delete',
            deleteConfirmTitle: 'Delete slide?',
            deleteConfirmMessage:
                '"{{name}}" will be removed from the info screen when you save.',
            imageHint:
                'Any image size works; landscape images look best on TVs.',
            fitLabel: 'Image fit',
            fitCover: 'Fill',
            fitContain: 'Fit',
            uploadImage: 'Upload images',
            uploadHint: 'Drag and drop images here, or browse files.',
            dropToAdd: 'Drop images to add them',
            onlyImages: 'Only image files can be added.',
            uploadFailed: 'Could not add the selected image.',
            maxSlides: 'Maximum {{max}} slides allowed.',
            previewTitle: 'TV preview',
            previewEmpty: 'No slides are shown on the TV yet',
            tvLinkTitle: 'TV link',
            tvLinkDescription:
                'Every TV that opens this link shows your slides.',
            copyLink: 'Copy link',
            linkCopied: 'TV link copied.',
            regenerate: 'Regenerate link',
            regenerateConfirmTitle: 'Create a new TV link?',
            regenerateConfirmMessage:
                'The current link stops working immediately, and every TV must be set up again with the new link.',
            linkRegenerated: 'New TV link created.',
            setupTitle: 'TV setup instructions',
            setupStep1: 'Open the browser on the TV or signage device.',
            setupStep2: 'Enter the TV link shown above.',
            setupStep3: 'Set the browser to launch this page on startup.',
            setupStep4: 'Switch to full-screen mode before leaving the TV.',
            publicNotFound: 'Info screen not found',
            publicLoading: 'Loading…',
            publicNoSlides: 'No active slides',
        },
        moduleMembers: {
            title: 'Members',
            count: '{{count}} members',
            countWithLimit: '{{count}} of {{limit}} members',
            edit: 'Edit members',
            addTitle: 'Add members',
            add: 'Add',
            remove: 'Remove',
            searchLabel: 'Search employees',
            searchPlaceholder: 'Search accounts by name, email or phone number',
            empty: 'No members have been added yet. Add at least one member to give them editing access to this module.',
            noAvailable: 'No matching accounts are available.',
            adminOnly:
                'Only organization administrators can change module members.',
            saving: 'Saving…',
            updated: 'Module members updated.',
            noEditingAccess:
                'Your account does not currently have editing access.',
            addSelfDescription:
                'Add yourself as a module member to create and edit content.',
            addSelfLimitReached:
                'The member limit must be increased or another member removed before you can add yourself.',
            addSelf: 'Add myself',
            addedSelf: 'Your account now has editing access.',
            limitReached:
                'The member limit ({{limit}}) is reached. Remove a member to add someone else.',
            formerEmployee: 'Former employee',
            formerEmployeeDescriptionShort:
                'This employee is no longer available ({{id}}).',
            formerEmployeeDescription:
                'This saved member no longer exists ({{id}}). Remove them and save the changes.',
        },
    },
    admin: {
        analytics: {
            departmentScope:
                'Shows the residents in this department, their relatives, and the employees and messages that belong to it.',
            fullTimeScope:
                'Login and activity count full-time employees only. The total includes part-time staff.',
            employeeTypeBreakdown:
                '{{fullTime}} full time · {{partTime}} part-time only',
            fullTimeEmployees: 'Full-time employees',
            allDepartments: 'All departments',
            departments: 'Departments',
            department: 'Department',
            coverageView: 'Chart view',
            coverageCurrent: 'Current',
            coverageMonthly: 'Monthly',
            employeeCoverageTitle: 'Full-time employee activity',
            employeeCoverageScope:
                'Current full-time employee accounts, with observed activity in the last 30 days.',
            employeeHistoryScope:
                'Saved monthly activity statuses for employees who are currently full time in this view.',
            employeeChartSummary:
                '{{active}}/{{total}} employees active in the last 30 days · {{percent}}%.',
            employeeCoverageNote:
                'Active: used Mundu in the last 30 days. Inactive: has logged in before, but not in the last 30 days. Invited: has never logged in. Each employee counts once.',
            employeeHistoryNote:
                "Status at the end of each month, for today's full-time employees. A month without a bar has no data.",
            coverageHistoryTitle: 'Monthly residents by main-contact activity',
            coverageHistoryScope:
                'Resident totals split by main-contact status, using the latest saved snapshot for each month.',
            coverageHistoryError:
                'Monthly coverage could not be loaded. Try refreshing.',
            coverageHistoryEmpty:
                'No complete snapshots yet. Monthly history starts when snapshots and activity tracking are available.',
            coverageHistoryUnavailable: 'No complete snapshot',
            snapshotTaken: 'Snapshot taken {{date}}',
            coverageHistorySnapshot: 'Snapshot date',
            coverageHistoryPartialLabel: 'Partial',
            coverageHistoryPartial:
                '* Current month, incomplete 30-day tracking, or an earlier final snapshot. Missing months have no bar.',
            coverageHistoryNote:
                "Residents' main-contact status at the end of each month. Past months don't change. A month without a bar has no data.",
            mainContactCoverageTitle: 'Residents by main-contact activity',
            mainContactChartSummary:
                '{{active}}/{{total}} residents have an active main contact · {{percent}}%.',
            mainContactChart_active: 'Active',
            mainContactChart_neverLoggedIn: 'Invited',
            mainContactChart_previouslyLoggedIn: 'Inactive',
            mainContactChart_noLinkedContact: 'No main contact',
            mainContactCoverageScope:
                'Current residents, with observed main-contact activity in the last 30 days.',
            withActiveMainContact: 'With an active main contact',
            withoutActiveMainContact: 'Without an active main contact',
            mainContact_noLinkedContact: 'No main contact linked',
            mainContact_neverLoggedIn:
                'Main contacts invited, none logged in yet',
            mainContact_previouslyLoggedIn: 'Previously logged in, none active',
            mainContactCoverageNote:
                'Each resident counts once, by their most active main contact. Active: used Mundu in the last 30 days. Inactive: has logged in before, but not recently. Invited: no main contact has logged in yet.',
            timeFreedTitle: 'Time freed',
            timeFreedScope:
                'Estimated for all original organization and relative messages in the selected period.',
            defaultAssumption:
                'Default assumption · {{minutes}} min per message',
            customAssumption: 'Your assumption · {{minutes}} min per message',
            editAssumption: 'Edit minutes saved per message',
            minutesPerMessage: 'Minutes saved per original message',
            applyAssumption: 'Apply',
            cancelAssumption: 'Cancel',
            resetAssumption: 'Use the 10-minute default',
            timeFreedCalculation:
                'An estimate: original messages × minutes saved per message. Change the minutes with the pencil.',
            organizationsAndStaff: 'Organizations and employees',
            organizationsAndStaff_organization: 'Departments and employees',
            residentsAndRelatives: 'Residents and relatives',
            relativeRoles: 'Relative roles',
            active_employees: 'Active full-time employees',
            active_mainContacts: 'Active main contacts',
            exportCsv: 'Export CSV',
            activeUsers: 'Observed active users',
            activeWindow:
                'Rolling 1, 7 and 30 days. Independent of the activity month selector.',
            active_employees_day: 'Active in the last 24 hours',
            active_employees_week: 'Active in the last 7 days',
            active_employees_month: 'Active in the last 30 days',
            active_mainContacts_day: 'Active in the last 24 hours',
            active_mainContacts_week: 'Active in the last 7 days',
            active_mainContacts_month: 'Active in the last 30 days',
            activeCoverage: 'Active main-contact coverage',
            activeCoverageNote:
                'Current residents with main contacts who used Mundu in the last 30 days.',
            coveredResidents: 'Residents with an active main contact',
            coveredCount: '{{count}} of {{total}} residents',
            activeMainContactLinks: '{{label}} active main contacts',
            activeMainContactLinks_one: '{{label}} active main contact',
            trackingSince: 'Activity tracking started {{date}}.',
            activityAppNote:
                "Mobile activity counts from the latest app version, so people still on an older version aren't counted yet.",
            trackingNotStarted: 'No tracked activity yet.',
            infoFor: 'Information about {{title}}',
            trackedActivityTitle: 'User activity and coverage',
            chooseDrilldownOrganization: 'Choose an organization',
            openNoStaffReplyInSupport:
                'Open the organization in read-only support mode with the No staff reply filter selected.',
            viewNoStaffReply: 'View conversations with no staff reply',
            openWithoutRelativesInSupport:
                'Open the organization in read-only support mode with residents without linked relatives highlighted.',
            viewResidentsWithoutRelatives:
                'View residents without linked relatives',
            openInvitedInSupport:
                'Open the organization in read-only support mode with the Invited filter selected.',
            viewNeverLoggedInEmployees: 'View invited employees',
            viewNeverLoggedInMainContacts:
                'View residents whose main contacts are all invited',
            activity: 'Activity',
            period: 'Activity period',
            allTime: 'All time',
            currentMonth: 'Current month',
            activityAsOf: 'Updated {{date}}.',
            previousPeriod:
                'Showing the previous period until the requested figures are available.',
            monthlyScope:
                'Applies to all activity below. Months follow Norwegian time, and deleted records are excluded.',
            comments: 'Comments',
            allDirections: 'From all senders in the selected period.',

            howCounted: 'How this is counted',
            selectedMonth: 'Selected month',
            timeFreedShort: 'Assumes 10 min saved per message',
            selectChartMonth: 'Select {{month}} · {{count}} original messages',
            trendTitle: 'Monthly messages',
            trendShort: 'Comments are counted separately.',
            audienceTitle: 'Message audiences',
            audienceNote: 'Who original messages were sent to.',
            responseShort:
                'Started in the selected period. Reply status as of today.',
            calendarShort: 'Created or answered in the selected period.',

            estimatedTimeFreed: 'Estimated time freed',
            timeFreedAssumption:
                'Assumes 10 minutes saved per original organization or relative message replacing a call. Replies and recipient counts are excluded. This is an estimate, not a measured saving.',
            title: 'Analytics',
            refresh: 'Refresh',
            homeType: 'Home type',
            allHomes: 'All homes',
            organizations: 'Organizations',
            allOrganizations: 'All organizations',
            reportingMonth: 'Reporting month',
            scopeNote:
                'Filters apply to the whole page. Leave organizations empty to include every home of the selected type.',
            scopeNote_organization:
                'Figures cover this organization only. The department filter applies to the whole page.',
            people: 'Organizations and people',
            residents: 'Residents',
            relatives: 'Relatives',
            employees: 'Employees',
            mainContacts: 'Main contacts',
            guardians: 'Guardians',
            guardianNote:
                'Relatives marked as a guardian in their profile. Counted separately from main contacts.',
            residentsWithMainContactCount:
                '{{count}} of {{total}} residents have at least one main contact linked.',
            relativeTypeBreakdown:
                '{{mainContacts}} main contacts · {{others}} other relatives',
            mainContactsPerResident:
                '{{value}} main contacts per resident on average.',
            mainContactsScope:
                'Login and activity for relatives count main contacts only. The total includes all relatives.',
            perResident: '{{value}} per resident',
            activationDetail:
                '{{activated}} have logged in · {{never}} never logged in · {{unknown}} unknown',
            residentConnections: 'Relatives per resident',
            linkedRelatives: '{{label}} linked relatives',
            linkedRelatives_one: '{{label}} linked relative',
            coverageNote: 'How many relatives are linked to each resident.',
            permissions: 'Roles and permissions',
            organizationAdmins: 'Organization administrators',
            employeeManagers: 'Employees who can add/edit employees',
            broadcasters: 'Employees who can send to all',
            familyRelatives: 'Relatives with family role',
            externalRelatives: 'Relatives with external role',
            permissionsNote: 'A person can have several roles.',
            messages: 'Messages',
            direction: 'Message direction',
            direction_all: 'All',
            direction_organization: 'From organization',
            direction_relative: 'From relatives',
            originalMessages: 'Original messages',
            messagesPerResident: '{{value}} per current resident',
            organizationReplies: 'From organization',
            relativeReplies: 'From relatives',
            recordedReads: 'Recorded recipient views',
            trendDescription:
                'Original messages in each of the last 12 months, ending in the current month',
            trendNote:
                'Messages that start a conversation. Comments are counted separately.',
            audienceAll: 'All recipients',
            audienceGroup: 'Group audience',
            audienceIndividual: 'Individual',
            readNote:
                'Counts distinct message–recipient pairs for original messages created in the selected period, as recorded now. One person seeing five messages counts five times; repeated views of the same message count once. Mobile message-list exposure can register a view. This does not confirm careful reading or notification delivery. Deleted messages and comments are excluded.',
            relativeConversations: 'Conversations started by relatives',
            answered: 'Replied by staff',
            unanswered: 'No staff reply',
            medianResponse: 'Median first response',
            hours: '{{value}} h',
            replied24h: 'Replied within 24 hours',
            replied48h: 'Replied within 48 hours',
            replied72h: 'Replied within 72 hours',
            eligibleThreads: 'Based on {{count}} answered conversations',
            conversationShare: '{{count}}/{{total}} conversations',
            responseNote:
                'Conversations relatives started in the selected period. A reply is the first comment from staff. Hours include nights and weekends. The 24, 48 and 72 hour shares only count conversations at least that old.',
            calendar: 'Calendar',
            eventsCreated: 'Calendar entries created',
            rsvpResponses: 'RSVP answers',
            eventType_residents: 'Open to all',
            eventType_internal: 'Internal',
            eventType_group: 'Group',
            eventType_relatives: 'Relatives',
            calendarNote:
                'Events created in the selected period, by type. A recurring event counts once. RSVP counts current Going and Not going answers.',
            byOrganization: 'By organization',
            organization: 'Organization',
            unknownType: 'Not specified',
            empty: 'No organizations match these filters.',
            empty_organization: 'No figures for this organization yet.',
            deduplicationNote:
                'People counts are current. Message counts use the selected period.',
            error: 'Analytics could not be loaded. Try refreshing.',
            loading: 'Loading analytics…',
            asOf: 'Updated {{date}}',
        },
        dashboard: {
            newOrganization: 'New organization',
            newAdmin: 'New admin',
            sectionTitle: 'Administrators',
        },
        notifications: {
            organizationCreated: 'Organization created.',
            organizationUpdated: 'Organization updated.',
            organizationDocumentsUpdated: 'Organization documents updated.',
            organizationDeleted: 'Organization deleted.',
            adminCreated: 'Admin created.',
            adminDeleted: 'Admin deleted.',
            adminPermissionsUpdated: 'Admin permissions updated.',
            genericError: 'Something went wrong.',
        },
        documents: {
            title: 'Documents',
            description:
                'Upload shared organization documents for residents, such as welcome information, maps, or practical guides.',
            folderNameLabel: 'Folder name',
            folderNamePlaceholder: 'Welcome info',
            uploadButton: 'Upload documents',
            saveButton: 'Save documents',
            removeButton: 'Remove',
            previewButton: 'Preview',
            previewLabel: 'Preview {{name}}',
            previewClose: 'Close',
            previewDownload: 'Download',
            previewOpenTab: 'Open in new tab',
            previewLoading: 'Loading document...',
            previewError: 'The document could not be loaded. Try again.',
            previewRetry: 'Retry',
            previewRenderError:
                'This browser could not display the document. Open it in a new tab or download it.',
            previewPdfHint:
                'If the PDF does not appear, open it in a new tab or download it.',
            previewUnsupported:
                'Preview is unavailable for this file type. Download it to view its contents.',
            empty: 'No documents uploaded yet.',
            originalName: 'Original file',
            displayName: 'Display name',
            displayNamePlaceholder: 'Shown in the app',
            openedBy: 'Opened by',
            residentViews_one: '{{count}} resident',
            residentViews_other: '{{count}} residents',
            relativeViews_one: '{{count}} relative',
            relativeViews_other: '{{count}} relatives',
            unsupportedType:
                'Only PDF, image, or Office document files are allowed.',
            fileTooLarge: 'Each document must be 10 MB or smaller.',
            folderNameRequired: 'Add a folder name before saving documents.',
        },
        forms: {
            createAdmin: {
                firstName: 'First name*',
                lastName: 'Last name*',
                email: 'Email*',
                initialPassword: 'Initial password*',
                submit: 'Create admin',
                supportPermission: 'Allow read-only support access',
            },
            createOrganization: {
                organizationDetailsTitle: 'Organization details',
                administratorDetailsTitle: 'Initial administrator',
                firstName: 'First name*',
                lastName: 'Last name*',
                phone: 'Phone number',
                email: 'Email*',
                initialPassword: 'Initial password*',
                shortName: 'Org. short name*',
                displayName: 'Org. display name*',
                licences: 'Number of licences*',
                type: 'Organization type',
                typeCareHome: 'Senior home',
                typeNursingHome: 'Nursing home',
                disableLogoDesignAdjustments:
                    'Show uploaded logo without color adjustments',
                disableLogoDesignAdjustmentsHelper:
                    'Use this for PNG logos that already include their own colors or background.',
                infoScreenEnabled: 'Enable info screen',
                infoScreenEnabledHelper:
                    'Shows the Info screen page for this organization.',
                infoScreenModuleHistoryTitle: 'Info screen history',
                analyticsModuleEnabled: 'Enable analytics module',
                analyticsModuleEnabledHelper:
                    'Gives this organization its own analytics. Administrators always have access; other employees only as module members.',
                analyticsModuleHistoryTitle: 'Analytics module history',
                appContentModuleEnabled: 'Enable documents module',
                appContentModuleEnabledHelper:
                    'Shows organization documents to residents and relatives in the Mundu app. Turning it off hides the documents without deleting them.',
                appContentModuleHistoryTitle: 'Documents module history',
                calendarModuleEnabled: 'Enable calendar module',
                calendarModuleEnabledHelper:
                    'Gives this organization access to the calendar and events module.',
                calendarModuleHistoryTitle: 'Calendar module history',
                calendarModuleHistoryEnabled: 'Enabled',
                calendarModuleHistoryDisabled: 'Disabled',
                submit: 'Create organization',
                update: 'Update organization',
                groupsTitle: 'Groups',
                groupsEmpty: 'No groups defined yet.',
                groupName: 'Group name',
                groupId: 'Group ID',
                groupLevelNameLabel: '{{levelName}} name',
                addGroup: 'Add group',
                removeGroup: 'Remove group',
                addGroupLevelNamed: 'Add {{levelName}}',
                removeGroupLevelNamed: 'Remove {{levelName}}',
                rolesTitle: 'Roles',
                rolesEmpty: 'No roles defined yet.',
                roleName: 'Role name',
                roleId: 'Role ID',
                roleGroups: 'Groups',
                rolePermissions: 'Permissions',
                rolePermissionsReadResidents: 'Read residents',
                rolePermissionsWriteResidents: 'Write residents',
                rolePermissionsReadEmployees: 'Read employees',
                rolePermissionsWriteEmployees: 'Write employees',
                rolePermissionsSendMessagesToAll: 'Send messages to all',
                rolePermissionsEmpty: 'No permissions selected yet.',
                addRole: 'Add role',
                removeRole: 'Remove role',
                hierarchyTitle: 'Group hierarchy',
                hierarchyEmpty: 'No hierarchy levels added yet.',
                addHierarchyLevel: 'Add level',
                addHierarchySublevel: 'Add sublevel',
                removeHierarchyLevel: 'Remove level',
                hierarchyLevelId: 'Level ID',
                hierarchyLevelName: 'Level name',
            },
        },
        list: {
            deleteButton: 'Delete',
            addSupportPermission: 'Enable support access',
            removeSupportPermission: 'Disable support access',
            fields: {
                name: 'Name:',
                phone: 'Phone number:',
                email: 'Email:',
                created: 'Created:',
                updated: 'Updated:',
            },
        },
        orgInfo: {
            memberLimitTitle: 'Module member limit',
            memberLimitHelper:
                'Maximum number of employees the organization can add to this module. {{count}} added so far. Leave empty for no limit.',
            memberLimitUnlimited: 'No limit',
            memberLimitInvalid: 'The member limit must be a positive number.',
            memberLimitBelowCurrent:
                'The limit cannot be lower than the current member count ({{count}}). Ask the organization administrator to remove members first.',
            fields: {
                superUser: 'Superuser:',
                phone: 'Phone number:',
                email: 'Email:',
                orgName: 'Org short name:',
                licences: 'Licences:',
                created: 'Created:',
                updated: 'Updated:',
            },
            editButton: 'Edit organization',
            enterSupportMode: 'Enter support mode',
            enteringSupportMode: 'Opening support mode…',
            deleteButton: 'Delete organization',
        },
    },
    creators: {
        legacyAdmin: {
            title: 'Delete original system admin account?',
            deleteSectionTitle: 'Delete system admin account',
            description:
                'Deleting {{name}} disables this login and removes it from the employee and module lists. Organization data and history will stay unchanged.',
            readOnlyDescription:
                "This is the organization's original system admin account. This profile cannot be edited.",
            selfDeletion:
                'Sign in with another system admin account to delete this login.',
            confirmLabel: "Type '{{value}}' to confirm",
            confirm: 'Delete system admin account',
            success: 'Original system admin account deleted.',
        },
        list: {
            roleNames: 'Role names',
            department: 'Department',
            missingReadEmployeesPermission:
                'Access denied: missing permission to read employees.',
        },
        form: {
            firstName: 'First name*',
            lastName: 'Last name*',
            phone: 'Phone number',
            email: 'Email',
            roleLabel: 'Role',
            departmentLabel: 'Department (optional)',
            employeeTypeLabel: 'Employee type',
            employeeTypeFullTime: 'Full time',
            employeeTypePartTime: 'Part time',
            noDepartment: 'No department',
            departmentHelp:
                'Used for staff reporting. Does not change roles or access.',
            departmentLoadError:
                'Departments could not be loaded. Try again later.',
            departmentLegacy: 'Legacy department: {{value}}',
            departmentLegacyHelp:
                'This old value is not a department assignment. Choose a department or clear it with No department.',
            departmentRemoved: 'Removed department',
            departmentRemovedHelp:
                'The previously assigned group was removed. This counts as No department. Choose another department or clear it.',
            departmentNestedHelp:
                'This group is no longer top-level. The existing assignment is preserved, but it cannot be chosen for new assignments.',
            rolePlaceholder: 'Select role',
            rolesEmpty: 'No roles defined for this organization.',
            roleRequired: 'Select a role to continue.',
            adminDescription:
                'System admins have full access to manage the organization.',
            adminSelfRemoval: 'You cannot remove your own system admin access.',
            submit: 'Send invitation',
        },
        notifications: {
            consumerAdded: 'User added',
            consumerRemoved: 'User removed',
            deleted: 'Employee deleted',
            created: 'Employee created.',
        },
        profile: {
            editTitle: 'Edit profile',
            deleteTitle: 'Delete employee?',
            deleteDescription:
                'Deleting a employee removes their access to Mundu and cannot be restored.',
            confirmLabel: "Type '{{value}}' to delete",
            deleteButton: 'Delete employee',
        },
    },
    users: {
        leafGroups: {
            navLabel: 'Groups',
            title: 'Group list',
            empty: 'No groups available yet.',
            parent: 'Parent',
            resident: 'Resident',
            id: 'Group ID',
        },
        form: {
            firstName: 'First name*',
            lastName: 'Last name*',
            password: 'Password*',
            confirmPassword: 'Confirm password*',
            username: 'Username*',
            submit: 'Create new resident',
            groupsLabel: 'Groups',
            groupsPlaceholder: 'Select groups',
            groupsEmpty: 'No groups available yet.',
            groupsAvailableEmpty: 'No available {{levelName}} yet.',
            groupsTopLevelPlaceholder: 'Select a top-level group',
            groupsTopLevelPlaceholderNamed: 'Select {{levelNames}}',
            groupsChildPlaceholder: 'Select a subgroup',
            groupsChildPlaceholderNamed: 'Select {{levelName}}',
            groupsSelectedLeaf: 'Selected group: {{value}}',
        },
        limit: {
            title: 'All licences are in use!',
            description: 'Need more licences?',
            contact: 'Contact us',
        },
        profile: {
            editTitle: 'Edit profile',
            password: 'Password',
            passwordDescription:
                'The password for {{name}} is shown below. Reset it to generate a new password instantly.',
            resetButton: 'Reset password',
            resetInfo:
                'Share this password securely. Reset whenever you need to issue a new one.',
            passwordLabel: 'Password:',
            passwordMissing: 'Not available',
            passwordResetSuccess: 'Password updated.',
            deleteTitle: 'Delete user?',
            deleteDescription:
                'Deleting a user removes access to Mundu and cannot be undone.',
            confirmLabel: "Type '{{value}}' to delete",
            deleteButton: 'Delete user',
            tabs: {
                images: 'Photo library',
                nextOfKin: 'Relatives',
                messages: 'Messages',
                sessions: 'Videos',
                profile: 'Profile',
            },
        },
        notifications: {
            created: 'Resident created.',
            deleted: '{{name}} has been deleted.',
            creatorAdded: 'Employee added',
            creatorRemoved: 'Employee removed',
        },
    },
    images: {
        actions: {
            add: 'Add photos',
            discard: 'Discard',
        },
        notifications: {
            deleted: 'Image deleted.',
        },
    },
    imageUpload: {
        dropzone: {
            add: 'Add photos',
            discard: 'Discard',
            invalidTypes:
                '{{count}} file has an unsupported format. Mundu only supports JPEG, JPG, PNG, and WebP.',
            invalidTypes_plural:
                '{{count}} files have unsupported formats. Mundu only supports JPEG, JPG, PNG, and WebP.',
            rejected: '{{count}} file was rejected.',
            rejected_plural: '{{count}} files were rejected.',
        },
        preview: {
            placeholder: 'Add a description',
        },
        consent: {
            title: 'Consent',
            description:
                'Mundu takes privacy seriously. Everyone shown in the photos must consent, or you must be authorized to consent on their behalf, before we can use the photos in our service. Read more about Mundu’s privacy policy',
            readMore: 'here.',
            checkbox: 'I consent',
            uploadLabel: 'Upload {{count}} photo',
            uploadLabel_plural: 'Upload {{count}} photos',
        },
    },
    sessions: {
        actions: {
            new: 'New video',
        },
        confirmDelete: 'Are you sure you want to delete this video?',
        notifications: {
            deleted: 'Video deleted.',
        },
    },
    login: {
        title: 'Sign in',
        description:
            'Use email or phone number together with your password to sign in to Mundu.',
        identifierLabel: 'Email or phone number',
        passwordLabel: 'Password',
        forgotPassword: 'Forgot your password?',
        submit: 'Sign in',
        submitting: 'Signing in…',
        genericError: 'Something went wrong. Please try again later.',
        passwordChangeRequired:
            'Enter a new password to activate your account before signing in.',
        newPasswordLabel: 'New password',
        confirmNewPasswordLabel: 'Confirm new password',
        newPasswordDescription:
            'The initial password works only once. Choose a new password to continue.',
        submitNewPassword: 'Save new password',
        useDifferentAccount: 'Use another account',
    },
    resetPassword: {
        title: 'Reset password',
        description:
            'Request a reset code via email or SMS and use it to choose a new password.',
        instructions:
            'Already have a code? Enter it together with your email or phone number to set a new password.',
        request: {
            heading: 'Request reset code',
            emailLabel: 'Email or phone number',
            submit: 'Send code',
            success: 'We sent a verification code to {{email}}.',
            genericError:
                'We could not send the reset email. Double-check the address and try again.',
        },
        reset: {
            heading: 'Set new password',
            codeLabel: 'Verification code',
            newPasswordLabel: 'New password',
            confirmPasswordLabel: 'Confirm password',
            submit: 'Update password',
            success: 'Password updated. You can now sign in.',
            genericError:
                'We could not reset the password. Check the code and try again.',
            useDifferentEmail: 'Use a different email',
        },
        backToLogin: 'Back to sign in',
    },
    notFound: {
        title: 'This page does not exist!',
        goHome: 'Go home',
        goToLanding: 'Go to Mundu',
    },
    faq: faqEn,
};

export default enTranslations;
